import { apiClient } from './api';
import type {
  AddressCatalogColonyLookupResponse,
  AddressCatalogLocationNormalizationResponse,
  AddressCatalogPostalCodeResponse,
  CancelContractPayload,
  CertificationStatusResponse,
  ContractOperatorProfileResponse,
  ActivateReturnIntentPayload,
  CreateContractAssignmentPayload,
  ShipmentContractAssignmentResponse,
  CreateContractInvitationResponse,
  CreateContractInvitationPayload,
  CreateQuotePayload,
  CreateShipmentRequestPayload,
  UpdateShipmentRequestSchedulePayload,
  GenericMessageResponse,
  LegalDocumentDeleteResponse,
  LegalDocumentsIndexResponse,
  LegalDocumentType,
  LegalDocumentUploadResponse,
  MapsPlaceReverseGeocodeResponse,
  MapsPlaceDetailsResponse,
  MapsPlacesAutocompleteResponse,
  MapsRouteEstimateResponse,
  OperationMode,
  OperatorHomeBaseResponse,
  OperatorLegalDocument,
  OperatorProfileResponse,
  OperatorProfileSaveResponse,
  OperatorBankAccountListResponse,
  OperatorBankAccountResponse,
  OperatorTrailerListResponse,
  OperatorTrailerResponse,
  OperatorTrailerPhotoListResponse,
  OperatorVehiclePhotoListResponse,
  OperatorVehicleListResponse,
  OperatorVehicleResponse,
  ProfileModeResponse,
  ReturnIntentResponse,
  ReturnIntentStatusResponse,
  SatCatalogResponse,
  SaveHomeBasePayload,
  ShipmentContractListResponse,
  ShipmentContractFileDeleteResponse,
  ShipmentContractFileDownloadPayload,
  ShipmentContractFileListResponse,
  ShipmentContractFileUploadResponse,
  ShipmentContractFileCategory,
  ShipmentContractFileScope,
  ShipmentContractListStatusFilter,
  ShipmentContractResponse,
  ShipmentInvitationDecisionResponse,
  ShipmentInvitationDeclineResponse,
  ShipmentInvitationAcceptResponse,
  ShipmentQuoteListResponse,
  ShipmentQuoteHistoryResponse,
  ShipmentQuoteResponse,
  ShipmentRequestListResponse,
  ShipmentRequestListStatusFilter,
  ShipmentRequestResponse,
  ContractPayment,
  ContractPaymentListResponse,
  ContractPaymentResponse,
  PaymentInfo,
  PaymentInfoResponse,
  UpdateContractPointsPayload,
  UpdateCargoInsurancePayload,
  UpdateProfileModeResponse,
  UploadPaymentProofPayload,
  UpsertOperatorBankAccountPayload,
  UpsertOperatorProfilePayload,
  UpsertOperatorTrailerPayload,
  UpsertOperatorVehiclePayload,
  TrailerPhoto,
  VehiclePhoto,
} from '../types/marketplace';
import type {
  CartaPorteItem,
  CartaPorteItemResponse,
} from '../types/cartaPorte';
import type {
  CreateUserRatingPayload,
  CreateUserRatingResponse,
  UserRating,
  UserRatingEligibility,
  UserRatingEligibilityResponse,
} from '../types/userProfile';

interface PaginationParams {
  page?: number;
  per_page?: number;
  status?: string;
}

interface MarketplaceAvailabilityApiError {
  status?: number;
  error_code?: string;
  message?: string;
  errors?: Record<string, string[]>;
}

const MAX_VEHICLE_PHOTO_COUNT = 10;
const MAX_TRAILER_PHOTO_COUNT = 10;

export class VehiclePhotoLimitError extends Error {
  readonly code = 'VEHICLE_PHOTO_LIMIT';
  readonly status = 422;

  constructor(message: string) {
    super(message);
    this.name = 'VehiclePhotoLimitError';
  }
}

export class TrailerPhotoLimitError extends Error {
  readonly code = 'TRAILER_PHOTO_LIMIT';
  readonly status = 422;

  constructor(message: string) {
    super(message);
    this.name = 'TrailerPhotoLimitError';
  }
}

export class VehicleInvalidError extends Error {
  readonly code = 'VEHICLE_INVALID';
  readonly status = 422;

  constructor(message: string) {
    super(message);
    this.name = 'VehicleInvalidError';
  }
}

export class QuoteVehicleLockedError extends Error {
  readonly code = 'QUOTE_VEHICLE_LOCKED';
  readonly status = 409;

  constructor(message: string) {
    super(message);
    this.name = 'QuoteVehicleLockedError';
  }
}

export class QuoteVehicleWeightExceededError extends Error {
  readonly code = 'QUOTE_VEHICLE_WEIGHT_EXCEEDED';
  readonly status = 422;

  constructor(message: string) {
    super(message);
    this.name = 'QuoteVehicleWeightExceededError';
  }
}

export class CargoInsuranceUpdateError extends Error {
  readonly status?: number;
  readonly code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'CargoInsuranceUpdateError';
    this.status = status;
    this.code = code;
  }
}

export class CargoInsuranceSelectionClosedError extends CargoInsuranceUpdateError {
  constructor(message: string) {
    super(message, 409, 'INSURANCE_SELECTION_CLOSED');
    this.name = 'CargoInsuranceSelectionClosedError';
  }
}

type RequestListQueryStatus = Exclude<ShipmentRequestListStatusFilter, 'all'>;
type ContractListQueryStatus = Exclude<ShipmentContractListStatusFilter, 'all'>;

function buildQuery(params: PaginationParams): string {
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined,
  );

  if (entries.length === 0) {
    return '';
  }

  return `?${entries
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join('&')}`;
}

function fieldErrors(
  error: MarketplaceAvailabilityApiError,
  field: string,
): string[] {
  return error.errors?.[field] ?? [];
}

function createAvailabilityError(
  error: MarketplaceAvailabilityApiError,
): Error | VehicleInvalidError | null {
  const driverErrors = fieldErrors(error, 'driver_user_id');
  const vehicleErrors = fieldErrors(error, 'operator_vehicle_id');
  const trailerErrors = fieldErrors(error, 'operator_trailer_id');

  if (driverErrors.includes('DRIVER_ALREADY_IN_ACTIVE_SHIFT')) {
    return new Error('El chofer ya tiene una jornada activa.');
  }

  if (driverErrors.includes('DRIVER_ALREADY_ASSIGNED_SHIFT')) {
    return new Error(
      'El chofer ya tiene una asignación pendiente en otro contrato.',
    );
  }

  if (driverErrors.includes('QUOTE_DRIVER_SHIFT_REQUIRED')) {
    return new Error(
      'El chofer debe iniciar Jornada con la unidad seleccionada antes de continuar.',
    );
  }

  if (driverErrors.includes('QUOTE_DRIVER_HAS_INCOMPATIBLE_SHIFT')) {
    return new Error(
      'La Jornada activa del chofer no coincide con la unidad o remolque seleccionados.',
    );
  }

  if (vehicleErrors.includes('VEHICLE_ALREADY_IN_ACTIVE_SHIFT')) {
    return new VehicleInvalidError('La unidad ya tiene una jornada activa.');
  }

  if (vehicleErrors.includes('QUOTE_RESOURCE_NOT_READY')) {
    return new Error(
      'La unidad seleccionada aún no está lista para cotizar. Completa sus datos y documentos requeridos en el perfil del operador.',
    );
  }

  if (vehicleErrors.includes('QUOTE_RESOURCE_UNAVAILABLE')) {
    return new VehicleInvalidError(
      error.message?.trim() || 'La unidad seleccionada ya no está disponible.',
    );
  }

  if (trailerErrors.includes('TRAILER_ALREADY_IN_ACTIVE_SHIFT')) {
    return new Error('El remolque ya tiene una jornada activa.');
  }

  if (trailerErrors.includes('QUOTE_RESOURCE_NOT_READY')) {
    return new Error(
      'El remolque seleccionado aún no está listo para cotizar. Completa sus datos y documentos requeridos en el perfil del operador.',
    );
  }

  if (trailerErrors.includes('QUOTE_RESOURCE_UNAVAILABLE')) {
    return new Error('El remolque seleccionado ya no está disponible.');
  }

  if (driverErrors.includes('QUOTE_RESOURCE_NOT_READY')) {
    return new Error(
      'El chofer seleccionado aún no está listo para cotizar. Completa sus datos y documentos requeridos en el perfil del operador.',
    );
  }

  return null;
}

function extractFilenameFromContentDisposition(
  contentDispositionHeader: string | null,
): string | null {
  if (!contentDispositionHeader) {
    return null;
  }

  const utf8FilenameMatch = contentDispositionHeader.match(
    /filename\*=UTF-8''([^;]+)/i,
  );
  if (utf8FilenameMatch?.[1]) {
    try {
      return decodeURIComponent(utf8FilenameMatch[1]);
    } catch {
      return utf8FilenameMatch[1];
    }
  }

  const quotedFilenameMatch =
    contentDispositionHeader.match(/filename="([^"]+)"/i);
  if (quotedFilenameMatch?.[1]) {
    return quotedFilenameMatch[1];
  }

  const rawFilenameMatch = contentDispositionHeader.match(/filename=([^;]+)/i);
  if (rawFilenameMatch?.[1]) {
    return rawFilenameMatch[1].trim();
  }

  return null;
}

function hasFileExtension(fileName: string): boolean {
  return /\.[a-z0-9]{1,10}$/i.test(fileName.trim());
}

function extensionFromMimeType(mimeType: string): string | null {
  const normalizedMime = mimeType.trim().toLowerCase();

  if (normalizedMime === '') {
    return null;
  }

  const knownExtensions: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'text/plain': 'txt',
  };

  return knownExtensions[normalizedMime] ?? null;
}

function normalizeDownloadFileName(fileName: string, mimeType: string): string {
  const trimmedName = fileName.trim();
  const safeFallback = trimmedName !== '' ? trimmedName : 'archivo';

  if (hasFileExtension(safeFallback)) {
    return safeFallback;
  }

  const extension = extensionFromMimeType(mimeType);
  if (!extension) {
    return safeFallback;
  }

  return `${safeFallback}.${extension}`;
}

export const marketplaceService = {
  getProfileMode: async (): Promise<ProfileModeResponse> => {
    return apiClient.get<ProfileModeResponse>('/api/profile/mode');
  },

  updateProfileMode: async (
    operationMode: OperationMode,
  ): Promise<UpdateProfileModeResponse> => {
    return apiClient.put<UpdateProfileModeResponse>('/api/profile/mode', {
      operation_mode: operationMode,
    });
  },

  getOperatorProfile: async (): Promise<OperatorProfileResponse> => {
    return apiClient.get<OperatorProfileResponse>('/api/operator/profile');
  },

  upsertOperatorProfile: async (
    payload: UpsertOperatorProfilePayload,
  ): Promise<OperatorProfileSaveResponse> => {
    return apiClient.post<OperatorProfileSaveResponse>(
      '/api/operator/profile',
      payload,
    );
  },

  createOperatorBankAccount: async (
    payload: UpsertOperatorBankAccountPayload,
  ): Promise<OperatorBankAccountResponse> => {
    return apiClient.post<OperatorBankAccountResponse>(
      '/api/operator/bank-accounts',
      payload,
    );
  },

  updateOperatorBankAccount: async (
    accountId: number,
    payload: UpsertOperatorBankAccountPayload,
  ): Promise<OperatorBankAccountResponse> => {
    return apiClient.put<OperatorBankAccountResponse>(
      `/api/operator/bank-accounts/${accountId}`,
      payload,
    );
  },

  deleteOperatorBankAccount: async (
    accountId: number,
  ): Promise<GenericMessageResponse> => {
    return apiClient.delete<GenericMessageResponse>(
      `/api/operator/bank-accounts/${accountId}`,
    );
  },

  getOperatorBankAccounts:
    async (): Promise<OperatorBankAccountListResponse> => {
      return apiClient.get<OperatorBankAccountListResponse>(
        '/api/operator/bank-accounts',
      );
    },

  getOperatorVehicles: async (): Promise<OperatorVehicleListResponse> => {
    return apiClient.get<OperatorVehicleListResponse>('/api/operator/vehicles');
  },

  createOperatorVehicle: async (
    payload: UpsertOperatorVehiclePayload,
  ): Promise<OperatorVehicleResponse> => {
    return apiClient.post<OperatorVehicleResponse>(
      '/api/operator/vehicles',
      payload,
    );
  },

  updateOperatorVehicle: async (
    vehicleId: number,
    payload: UpsertOperatorVehiclePayload,
  ): Promise<OperatorVehicleResponse> => {
    return apiClient.put<OperatorVehicleResponse>(
      `/api/operator/vehicles/${vehicleId}`,
      payload,
    );
  },

  deleteOperatorVehicle: async (
    vehicleId: number,
  ): Promise<GenericMessageResponse> => {
    return apiClient.delete<GenericMessageResponse>(
      `/api/operator/vehicles/${vehicleId}`,
    );
  },

  getVehiclePhotos: async (vehicleId: number): Promise<VehiclePhoto[]> => {
    const response = await apiClient.get<OperatorVehiclePhotoListResponse>(
      `/api/operator/vehicles/${vehicleId}/photos`,
    );

    return response.data;
  },

  uploadVehiclePhotos: async (
    vehicleId: number,
    files: File[],
  ): Promise<VehiclePhoto[]> => {
    if (files.length === 0) {
      throw new Error('Selecciona al menos una foto para cargar.');
    }

    const formData = new FormData();

    for (const file of files.slice(0, MAX_VEHICLE_PHOTO_COUNT)) {
      formData.append('photos[]', file);
    }

    try {
      const response =
        await apiClient.postForm<OperatorVehiclePhotoListResponse>(
          `/api/operator/vehicles/${vehicleId}/photos`,
          formData,
        );

      return response.data;
    } catch (error) {
      const apiError = error as {
        status?: number;
        error_code?: string;
        message?: string;
      };

      if (
        apiError.status === 422 &&
        apiError.error_code === 'VEHICLE_PHOTO_LIMIT'
      ) {
        const message =
          typeof apiError.message === 'string' && apiError.message.trim() !== ''
            ? apiError.message
            : 'La unidad alcanzó el límite de fotos permitidas.';

        throw new VehiclePhotoLimitError(message);
      }

      throw error;
    }
  },

  deleteVehiclePhoto: async (
    vehicleId: number,
    photoId: number,
  ): Promise<void> => {
    await apiClient.delete(
      `/api/operator/vehicles/${vehicleId}/photos/${photoId}`,
    );
  },

  reorderVehiclePhotos: async (
    vehicleId: number,
    order: number[],
  ): Promise<VehiclePhoto[]> => {
    const response = await apiClient.patch<OperatorVehiclePhotoListResponse>(
      `/api/operator/vehicles/${vehicleId}/photos/reorder`,
      { order },
    );

    return response.data;
  },

  // Legal Documents
  getLegalDocuments: async (
    vehicleId?: number,
  ): Promise<LegalDocumentsIndexResponse> => {
    const params = vehicleId ? `?vehicle_id=${vehicleId}` : '';
    return apiClient.get<LegalDocumentsIndexResponse>(
      `/api/operator/legal-documents${params}`,
    );
  },

  uploadLegalDocument: async (
    documentType: LegalDocumentType,
    file: File,
    expirationDate?: string,
    vehicleId?: number,
  ): Promise<LegalDocumentUploadResponse> => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('file', file);

    if (expirationDate) {
      formData.append('expiration_date', expirationDate);
    }

    if (vehicleId) {
      formData.append('vehicle_id', String(vehicleId));
    }

    return apiClient.postForm<LegalDocumentUploadResponse>(
      '/api/operator/legal-documents',
      formData,
    );
  },

  replaceLegalDocument: async (
    documentId: number,
    file: File,
    expirationDate?: string,
  ): Promise<LegalDocumentUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (expirationDate) formData.append('expiration_date', expirationDate);
    return apiClient.postForm<LegalDocumentUploadResponse>(
      `/api/operator/legal-documents/${documentId}/replace`,
      formData,
    );
  },

  getLegalDocument: async (
    documentId: number,
  ): Promise<{ message: string; data: OperatorLegalDocument }> => {
    return apiClient.get(`/api/operator/legal-documents/${documentId}`);
  },

  downloadLegalDocument: async (
    documentId: number,
  ): Promise<{ blob: Blob; headers: Headers }> => {
    return apiClient.getBlob(
      `/api/operator/legal-documents/${documentId}/download`,
    );
  },

  deleteLegalDocument: async (
    documentId: number,
  ): Promise<LegalDocumentDeleteResponse> => {
    return apiClient.delete<LegalDocumentDeleteResponse>(
      `/api/operator/legal-documents/${documentId}`,
    );
  },

  getCertificationStatus: async (): Promise<CertificationStatusResponse> => {
    return apiClient.get<CertificationStatusResponse>(
      '/api/operator/legal-documents/certification-status',
    );
  },

  getActiveReturnIntent: async (): Promise<ReturnIntentResponse> => {
    return apiClient.get<ReturnIntentResponse>(
      '/api/marketplace/return-intents/active',
    );
  },

  getReturnIntentStatus: async (): Promise<ReturnIntentStatusResponse> => {
    return apiClient.get<ReturnIntentStatusResponse>(
      '/api/marketplace/return-intents/status',
    );
  },

  activateReturnIntent: async (
    payload: ActivateReturnIntentPayload,
  ): Promise<ReturnIntentResponse> => {
    return apiClient.post<ReturnIntentResponse>(
      '/api/marketplace/return-intents',
      payload,
    );
  },

  cancelReturnIntent: async (
    intentId: number,
  ): Promise<ReturnIntentResponse> => {
    return apiClient.delete<ReturnIntentResponse>(
      `/api/marketplace/return-intents/${intentId}`,
    );
  },

  saveHomeBase: async (
    payload: SaveHomeBasePayload,
  ): Promise<OperatorHomeBaseResponse> => {
    return apiClient.put<OperatorHomeBaseResponse>(
      '/api/marketplace/operator/home-base',
      payload,
    );
  },

  clearHomeBase: async (): Promise<GenericMessageResponse> => {
    return apiClient.delete<GenericMessageResponse>(
      '/api/marketplace/operator/home-base',
    );
  },

  createShipmentRequest: async (
    payload: CreateShipmentRequestPayload,
  ): Promise<ShipmentRequestResponse> => {
    return apiClient.post<ShipmentRequestResponse>('/api/requests', payload);
  },

  updateShipmentRequest: async (
    requestId: number,
    payload: CreateShipmentRequestPayload,
  ): Promise<ShipmentRequestResponse> => {
    return apiClient.put<ShipmentRequestResponse>(
      `/api/requests/${requestId}`,
      payload,
    );
  },

  updateShipmentRequestSchedule: async (
    requestId: number,
    payload: UpdateShipmentRequestSchedulePayload,
  ): Promise<ShipmentRequestResponse> => {
    return apiClient.patch<ShipmentRequestResponse>(
      `/api/requests/${requestId}/schedule`,
      payload,
    );
  },

  publishShipmentRequest: async (
    requestId: number,
  ): Promise<ShipmentRequestResponse> => {
    return apiClient.post<ShipmentRequestResponse>(
      `/api/requests/${requestId}/post`,
      {},
    );
  },

  cancelShipmentRequest: async (
    requestId: number,
  ): Promise<ShipmentRequestResponse> => {
    return apiClient.post<ShipmentRequestResponse>(
      `/api/requests/${requestId}/cancel`,
      {},
    );
  },

  duplicateShipmentRequest: async (
    requestId: number,
  ): Promise<ShipmentRequestResponse> => {
    return apiClient.post<ShipmentRequestResponse>(
      `/api/requests/${requestId}/duplicate`,
      {},
    );
  },

  getMyRequests: async (
    params: PaginationParams & { status?: RequestListQueryStatus } = {},
  ): Promise<ShipmentRequestListResponse> => {
    return apiClient.get<ShipmentRequestListResponse>(
      `/api/requests/mine${buildQuery(params)}`,
    );
  },

  getFeedRequests: async (
    params: PaginationParams = {},
  ): Promise<ShipmentRequestListResponse> => {
    return apiClient.get<ShipmentRequestListResponse>(
      `/api/requests/feed${buildQuery(params)}`,
    );
  },

  discardFeedRequest: async (
    requestId: number,
  ): Promise<GenericMessageResponse> => {
    return apiClient.post<GenericMessageResponse>(
      `/api/requests/${requestId}/discard`,
      {},
    );
  },

  getRequestDetail: async (
    requestId: number,
  ): Promise<ShipmentRequestResponse> => {
    return apiClient.get<ShipmentRequestResponse>(`/api/requests/${requestId}`);
  },

  updateCargoInsurance: async (
    requestId: number,
    payload: UpdateCargoInsurancePayload,
  ): Promise<ShipmentRequestResponse> => {
    try {
      return await apiClient.patch<ShipmentRequestResponse>(
        `/api/requests/${requestId}/cargo-insurance`,
        payload,
      );
    } catch (error) {
      const apiError = error as MarketplaceAvailabilityApiError;
      const message =
        apiError.message?.trim() ||
        'No se pudo guardar la selección de seguro. Inténtalo nuevamente.';

      if (
        apiError.status === 409 &&
        apiError.error_code === 'INSURANCE_SELECTION_CLOSED'
      ) {
        throw new CargoInsuranceSelectionClosedError(
          'El seguimiento ya inició o el servicio dejó de ser elegible. La ventana de seguro está cerrada.',
        );
      }

      throw new CargoInsuranceUpdateError(
        message,
        apiError.status,
        apiError.error_code,
      );
    }
  },

  createQuote: async (
    requestId: number,
    payload: CreateQuotePayload,
  ): Promise<ShipmentQuoteResponse> => {
    try {
      return await apiClient.post<ShipmentQuoteResponse>(
        `/api/requests/${requestId}/quotes`,
        payload,
      );
    } catch (error) {
      const apiError = error as MarketplaceAvailabilityApiError;

      if (
        apiError.status === 422 &&
        apiError.error_code === 'QUOTE_VEHICLE_WEIGHT_EXCEEDED'
      ) {
        throw new QuoteVehicleWeightExceededError(
          apiError.message?.trim() ||
            'La unidad seleccionada excede el peso de su capacidad. Selecciona una unidad con mayor capacidad.',
        );
      }

      if (apiError.status === 422) {
        const availabilityError = createAvailabilityError(apiError);
        if (availabilityError) {
          throw availabilityError;
        }
      }

      if (
        apiError.status === 409 &&
        apiError.error_code === 'QUOTE_VEHICLE_LOCKED'
      ) {
        throw new QuoteVehicleLockedError(
          apiError.message?.trim() ||
            'La cotización ya no permite cambiar la unidad.',
        );
      }

      throw error;
    }
  },

  getRequestQuotes: async (
    requestId: number,
  ): Promise<ShipmentQuoteListResponse> => {
    return apiClient.get<ShipmentQuoteListResponse>(
      `/api/requests/${requestId}/quotes`,
    );
  },

  getRequestQuoteHistory: async (
    requestId: number,
  ): Promise<ShipmentQuoteHistoryResponse> => {
    return apiClient.get<ShipmentQuoteHistoryResponse>(
      `/api/requests/${requestId}/quote-history`,
    );
  },

  selectQuote: async (quoteId: number): Promise<ShipmentContractResponse> => {
    return apiClient.post<ShipmentContractResponse>(
      `/api/quotes/${quoteId}/select`,
      {},
    );
  },

  getMyContracts: async (
    params: PaginationParams & { status?: ContractListQueryStatus } = {},
  ): Promise<ShipmentContractListResponse> => {
    return apiClient.get<ShipmentContractListResponse>(
      `/api/contracts/mine${buildQuery(params)}`,
    );
  },

  getContractDetail: async (
    contractId: number,
  ): Promise<ShipmentContractResponse> => {
    return apiClient.get<ShipmentContractResponse>(
      `/api/contracts/${contractId}`,
    );
  },

  getContractRatingEligibility: async (
    contractId: number,
  ): Promise<UserRatingEligibility> => {
    const response = await apiClient.get<UserRatingEligibilityResponse>(
      `/api/contracts/${contractId}/ratings/eligibility`,
    );

    return response.data;
  },

  createContractRating: async (
    contractId: number,
    payload: CreateUserRatingPayload,
  ): Promise<UserRating> => {
    const response = await apiClient.post<CreateUserRatingResponse>(
      `/api/contracts/${contractId}/ratings`,
      payload,
    );

    return response.data;
  },

  getContractOperatorProfile: async (
    contractId: number,
  ): Promise<ContractOperatorProfileResponse> => {
    return apiClient.get<ContractOperatorProfileResponse>(
      `/api/contracts/${contractId}/operator-profile`,
    );
  },

  createContractAssignment: async (
    contractId: number,
    payload: CreateContractAssignmentPayload,
  ): Promise<ShipmentContractAssignmentResponse> => {
    try {
      return await apiClient.post<ShipmentContractAssignmentResponse>(
        `/api/contracts/${contractId}/assignments`,
        payload,
      );
    } catch (error) {
      const apiError = error as MarketplaceAvailabilityApiError;

      if (apiError.status === 422) {
        const availabilityError = createAvailabilityError(apiError);
        if (availabilityError) {
          throw availabilityError;
        }
      }

      throw error;
    }
  },

  getContractFiles: async (
    contractId: number,
  ): Promise<ShipmentContractFileListResponse> => {
    return apiClient.get<ShipmentContractFileListResponse>(
      `/api/contracts/${contractId}/files`,
    );
  },

  uploadContractFiles: async (
    contractId: number,
    files: File[],
    category: ShipmentContractFileCategory = 'documento',
    scope: ShipmentContractFileScope = 'general',
  ): Promise<ShipmentContractFileUploadResponse> => {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files[]', file);
    }

    formData.append('category', category);
    formData.append('scope', scope);

    return apiClient.postForm<ShipmentContractFileUploadResponse>(
      `/api/contracts/${contractId}/files`,
      formData,
    );
  },

  downloadContractFile: async (
    contractId: number,
    fileId: number,
    defaultFileName?: string,
  ): Promise<ShipmentContractFileDownloadPayload> => {
    const { blob, headers } = await apiClient.getBlob(
      `/api/contracts/${contractId}/files/${fileId}/download`,
    );
    const contentDisposition = headers.get('content-disposition');
    const extractedFilename =
      extractFilenameFromContentDisposition(contentDisposition);
    const fallbackName =
      defaultFileName?.trim() || `contrato-${contractId}-archivo-${fileId}`;
    const finalFileName = normalizeDownloadFileName(
      extractedFilename || fallbackName,
      blob.type,
    );

    return {
      blob,
      fileName: finalFileName,
    };
  },

  deleteContractFile: async (
    contractId: number,
    fileId: number,
  ): Promise<ShipmentContractFileDeleteResponse> => {
    return apiClient.delete<ShipmentContractFileDeleteResponse>(
      `/api/contracts/${contractId}/files/${fileId}`,
    );
  },

  updateContractPoints: async (
    contractId: number,
    payload: UpdateContractPointsPayload,
  ): Promise<ShipmentContractResponse> => {
    return apiClient.put<ShipmentContractResponse>(
      `/api/contracts/${contractId}/points`,
      payload,
    );
  },

  cancelContract: async (
    contractId: number,
    payload: CancelContractPayload,
  ): Promise<ShipmentContractResponse> => {
    return apiClient.post<ShipmentContractResponse>(
      `/api/contracts/${contractId}/cancel`,
      payload,
    );
  },

  createContractInvitation: async (
    contractId: number,
    payload: CreateContractInvitationPayload,
  ): Promise<CreateContractInvitationResponse> => {
    return apiClient.post<CreateContractInvitationResponse>(
      `/api/contracts/${contractId}/invitations`,
      payload,
    );
  },

  decideInvitation: async (
    invitationId: number,
    decision: 'accept' | 'decline',
  ): Promise<ShipmentInvitationDecisionResponse> => {
    return apiClient.post<ShipmentInvitationDecisionResponse>(
      `/api/contracts/invitations/${invitationId}/decision`,
      { decision },
    );
  },

  acceptInvitation: async (
    token: string,
  ): Promise<ShipmentInvitationAcceptResponse> => {
    return apiClient.post<ShipmentInvitationAcceptResponse>(
      `/api/contracts/invitations/${token}/accept`,
      {},
    );
  },

  declineInvitation: async (
    token: string,
  ): Promise<ShipmentInvitationDeclineResponse> => {
    return apiClient.post<ShipmentInvitationDeclineResponse>(
      `/api/contracts/invitations/${token}/decline`,
      {},
    );
  },

  autocompletePlaces: async (
    query: string,
    sessionToken?: string,
  ): Promise<MapsPlacesAutocompleteResponse> => {
    const params = new URLSearchParams({ q: query });

    if (sessionToken) {
      params.set('session_token', sessionToken);
    }

    return apiClient.get<MapsPlacesAutocompleteResponse>(
      `/api/maps/places/autocomplete?${params.toString()}`,
    );
  },

  getPlaceDetails: async (
    placeId: string,
    sessionToken?: string,
  ): Promise<MapsPlaceDetailsResponse> => {
    const params = new URLSearchParams({ place_id: placeId });

    if (sessionToken) {
      params.set('session_token', sessionToken);
    }

    return apiClient.get<MapsPlaceDetailsResponse>(
      `/api/maps/places/details?${params.toString()}`,
    );
  },

  reverseGeocodePlace: async (
    lat: number,
    lng: number,
  ): Promise<MapsPlaceReverseGeocodeResponse> => {
    const params = new URLSearchParams({
      lat: String(lat),
      lng: String(lng),
    });

    return apiClient.get<MapsPlaceReverseGeocodeResponse>(
      `/api/maps/places/reverse-geocode?${params.toString()}`,
    );
  },

  getRouteEstimate: async (
    originLat: number,
    originLng: number,
    destinationLat: number,
    destinationLng: number,
  ): Promise<MapsRouteEstimateResponse> => {
    const params = new URLSearchParams({
      origin_lat: String(originLat),
      origin_lng: String(originLng),
      destination_lat: String(destinationLat),
      destination_lng: String(destinationLng),
    });

    return apiClient.get<MapsRouteEstimateResponse>(
      `/api/maps/route-estimate?${params.toString()}`,
    );
  },

  getAddressCatalogByPostalCode: async (
    postalCode: string,
  ): Promise<AddressCatalogPostalCodeResponse> => {
    return apiClient.get<AddressCatalogPostalCodeResponse>(
      `/api/address-catalog/postal-codes/${encodeURIComponent(postalCode)}`,
    );
  },

  searchAddressCatalogByColony: async (
    colonyQuery: string,
    options: {
      state?: string | null;
      municipality?: string | null;
      city?: string | null;
    } = {},
  ): Promise<AddressCatalogColonyLookupResponse> => {
    const params = new URLSearchParams({
      query: colonyQuery,
    });

    const trimmedState =
      typeof options.state === 'string' ? options.state.trim() : '';
    if (trimmedState !== '') {
      params.set('state', trimmedState);
    }

    const trimmedMunicipality =
      typeof options.municipality === 'string'
        ? options.municipality.trim()
        : '';
    if (trimmedMunicipality !== '') {
      params.set('municipality', trimmedMunicipality);
    }

    const trimmedCity =
      typeof options.city === 'string' ? options.city.trim() : '';
    if (trimmedCity !== '') {
      params.set('city', trimmedCity);
    }

    return apiClient.get<AddressCatalogColonyLookupResponse>(
      `/api/address-catalog/colonies?${params.toString()}`,
    );
  },

  normalizeAddressCatalogLocation: async (options: {
    state?: string | null;
    municipality?: string | null;
    city?: string | null;
  }): Promise<AddressCatalogLocationNormalizationResponse> => {
    const params = new URLSearchParams();

    const trimmedState =
      typeof options.state === 'string' ? options.state.trim() : '';
    if (trimmedState !== '') {
      params.set('state', trimmedState);
    }

    const trimmedMunicipality =
      typeof options.municipality === 'string'
        ? options.municipality.trim()
        : '';
    if (trimmedMunicipality !== '') {
      params.set('municipality', trimmedMunicipality);
    }

    const trimmedCity =
      typeof options.city === 'string' ? options.city.trim() : '';
    if (trimmedCity !== '') {
      params.set('city', trimmedCity);
    }

    return apiClient.get<AddressCatalogLocationNormalizationResponse>(
      `/api/address-catalog/normalize-location?${params.toString()}`,
    );
  },

  // SAT Catalogs (public, no auth needed)
  getSatCatalog: async (catalog: string): Promise<SatCatalogResponse> => {
    return apiClient.get<SatCatalogResponse>(`/api/catalogs/sat/${catalog}`);
  },

  // Operator Trailers
  getOperatorTrailers: async (): Promise<OperatorTrailerListResponse> => {
    return apiClient.get<OperatorTrailerListResponse>('/api/operator/trailers');
  },

  createOperatorTrailer: async (
    payload: UpsertOperatorTrailerPayload,
  ): Promise<OperatorTrailerResponse> => {
    return apiClient.post<OperatorTrailerResponse>(
      '/api/operator/trailers',
      payload,
    );
  },

  updateOperatorTrailer: async (
    trailerId: number,
    payload: UpsertOperatorTrailerPayload,
  ): Promise<OperatorTrailerResponse> => {
    return apiClient.put<OperatorTrailerResponse>(
      `/api/operator/trailers/${trailerId}`,
      payload,
    );
  },

  deleteOperatorTrailer: async (
    trailerId: number,
  ): Promise<GenericMessageResponse> => {
    return apiClient.delete<GenericMessageResponse>(
      `/api/operator/trailers/${trailerId}`,
    );
  },

  getTrailerPhotos: async (trailerId: number): Promise<TrailerPhoto[]> => {
    const response = await apiClient.get<OperatorTrailerPhotoListResponse>(
      `/api/operator/trailers/${trailerId}/photos`,
    );

    return response.data;
  },

  uploadTrailerPhotos: async (
    trailerId: number,
    files: File[],
  ): Promise<TrailerPhoto[]> => {
    if (files.length === 0) {
      throw new Error('Selecciona al menos una foto para cargar.');
    }

    const formData = new FormData();

    for (const file of files.slice(0, MAX_TRAILER_PHOTO_COUNT)) {
      formData.append('photos[]', file);
    }

    try {
      const response =
        await apiClient.postForm<OperatorTrailerPhotoListResponse>(
          `/api/operator/trailers/${trailerId}/photos`,
          formData,
        );

      return response.data;
    } catch (error) {
      const apiError = error as {
        status?: number;
        error_code?: string;
        message?: string;
      };

      if (
        apiError.status === 422 &&
        apiError.error_code === 'TRAILER_PHOTO_LIMIT'
      ) {
        const message =
          typeof apiError.message === 'string' && apiError.message.trim() !== ''
            ? apiError.message
            : 'El remolque alcanzó el límite de fotos permitidas.';

        throw new TrailerPhotoLimitError(message);
      }

      throw error;
    }
  },

  deleteTrailerPhoto: async (
    trailerId: number,
    photoId: number,
  ): Promise<void> => {
    await apiClient.delete(
      `/api/operator/trailers/${trailerId}/photos/${photoId}`,
    );
  },

  reorderTrailerPhotos: async (
    trailerId: number,
    order: number[],
  ): Promise<TrailerPhoto[]> => {
    const response = await apiClient.patch<OperatorTrailerPhotoListResponse>(
      `/api/operator/trailers/${trailerId}/photos/reorder`,
      { order },
    );

    return response.data;
  },

  // Contract Carta Porte (acceso para participantes: client + operator)
  getContractCartaPorte: async (
    contractId: number,
  ): Promise<CartaPorteItem | null> => {
    const response = await apiClient.get<CartaPorteItemResponse>(
      `/api/contracts/${contractId}/carta-porte`,
    );
    return response.data ?? null;
  },

  downloadContractCartaPorteXml: async (contractId: number): Promise<Blob> => {
    const { blob } = await apiClient.getBlob(
      `/api/contracts/${contractId}/carta-porte/xml`,
    );
    return blob;
  },

  downloadContractCartaPortePdf: async (contractId: number): Promise<Blob> => {
    const { blob } = await apiClient.getBlob(
      `/api/contracts/${contractId}/carta-porte/pdf`,
    );
    return blob;
  },

  downloadContractProviderFeeInvoiceXml: async (
    contractId: number,
  ): Promise<Blob> => {
    const { blob } = await apiClient.getBlob(
      `/api/contracts/${contractId}/provider-fee-invoice/xml`,
    );
    return blob;
  },

  // Contract Payments
  getContractPaymentInfo: async (contractId: number): Promise<PaymentInfo> => {
    const response = await apiClient.get<PaymentInfoResponse>(
      `/api/contracts/${contractId}/payment-info`,
    );
    return response.data;
  },

  uploadPaymentProof: async (
    contractId: number,
    payload: UploadPaymentProofPayload,
  ): Promise<ContractPayment> => {
    const formData = new FormData();
    formData.append('proof_file', payload.proof_file);
    formData.append('amount', String(payload.amount));
    if (payload.payment_method)
      formData.append('payment_method', payload.payment_method);
    if (payload.payment_reference)
      formData.append('payment_reference', payload.payment_reference);
    if (payload.notes) formData.append('notes', payload.notes);

    const response = await apiClient.postForm<ContractPaymentResponse>(
      `/api/contracts/${contractId}/payments`,
      formData,
    );
    return response.data;
  },

  getContractPayments: async (
    contractId: number,
  ): Promise<ContractPayment[]> => {
    const response = await apiClient.get<ContractPaymentListResponse>(
      `/api/contracts/${contractId}/payments`,
    );
    return response.data;
  },

  downloadPaymentProof: async (
    contractId: number,
    paymentId: number,
  ): Promise<Blob> => {
    const { blob } = await apiClient.getBlob(
      `/api/contracts/${contractId}/payments/${paymentId}/proof`,
    );
    return blob;
  },
};
